/**
 * BucketImportView — the 'import' mode layout for a single supplier bucket.
 *
 * Left rail: per-file extraction status (parsed → extracted → done).
 * Right rail: SupplierExtractor surfacing the accumulated diff fields
 *   plus the Verwerfen / Anwenden footer (kept identical to the v3 review UI).
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconCheck, IconCircle, IconClock, IconX } from "@tabler/icons-react"
import clsx from "clsx"
import type { PendingDocumentOut } from "../../../types"
import { SupplierExtractor, type ExtractedField } from "./SupplierExtractor"

export type FileExtractionStatus =
  | "pending"
  | "parsing"
  | "extracting"
  | "done"
  | "failed"

export interface FileExtractionState {
  doc: PendingDocumentOut
  status: FileExtractionStatus
  error?: string
}

interface Props {
  supplierName: string
  supplierPk: string | null
  files: FileExtractionState[]
  fields: ExtractedField[]
  onFieldEdit: (primitiveId: string, value: string | null) => void
  onVerwerfen: () => void
  onUebernehmen: () => void
  onBack: () => void
  isCommitting: boolean
  error?: string | null
}

export function BucketImportView({
  supplierName,
  supplierPk,
  files,
  fields,
  onFieldEdit,
  onVerwerfen,
  onUebernehmen,
  onBack,
  isCommitting,
  error,
}: Props) {
  const { translate } = useTranslation()
  const allDone = files.every(
    (f) => f.status === "done" || f.status === "failed",
  )

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-grey4 px-6 py-4">
        <button
          onClick={onBack}
          disabled={isCommitting}
          className="rounded p-1 text-grey5 hover:bg-grey3 hover:text-white disabled:opacity-40"
          title={translate("QMS/SUPPLIER/BACK_TO_BUCKET_OVERVIEW")}
        >
          ←
        </button>
        <div>
          <p className="text-xs uppercase tracking-wide text-grey5">
            {translate("QMS/SUPPLIER/IMPORTING_LABEL")}
          </p>
          <p className="text-sm font-medium text-white">
            {supplierName}
            {supplierPk && supplierPk !== supplierName && (
              <span className="font-mono ml-2 text-xs text-grey5">
                {supplierPk}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Body — two columns */}
      <div className="flex min-h-0 flex-1">
        {/* Left: per-file status */}
        <div className="w-72 shrink-0 overflow-y-auto border-r border-grey4 px-4 py-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-grey5">
            {translate("QMS/SUPPLIER/PROCESSED_DOCUMENTS", { count: files.length })}
          </p>
          <div className="space-y-1">
            {files.map((f) => (
              <FileStatusRow key={f.doc.id} state={f} />
            ))}
          </div>
        </div>

        {/* Right: extracted fields + commit footer */}
        <div className="min-w-0 flex-1">
          {allDone ? (
            <SupplierExtractor
              filename={translate("QMS/SUPPLIER/DOCUMENT_COUNT", {
                count: files.length,
                documentLabel:
                  files.length === 1
                    ? translate("QMS/SUPPLIER/DOCUMENT_SINGULAR")
                    : translate("QMS/SUPPLIER/DOCUMENT_PLURAL"),
              })}
              supplierName={supplierName}
              fields={fields}
              onFieldEdit={onFieldEdit}
              onVerwerfen={onVerwerfen}
              onUebernehmen={onUebernehmen}
              isCommitting={isCommitting}
              error={error}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="space-y-3 text-center">
                <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-grey4 border-t-yellow1" />
                <p className="text-sm text-grey5">{translate("QMS/SUPPLIER/EXTRACTING_FIELDS")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function FileStatusRow({ state }: { state: FileExtractionState }) {
  const { translate } = useTranslation()
  const Icon =
    state.status === "done"
      ? IconCheck
      : state.status === "failed"
        ? IconX
        : state.status === "pending"
          ? IconCircle
          : IconClock

  const tint =
    state.status === "done"
      ? "text-success"
      : state.status === "failed"
        ? "text-red1"
        : state.status === "pending"
          ? "text-grey5"
          : "text-yellow1"

  const label = (
    {
      pending: translate("QMS/SUPPLIER/STATUS_PENDING"),
      parsing: translate("QMS/SUPPLIER/STATUS_PARSING"),
      extracting: translate("QMS/SUPPLIER/STATUS_EXTRACTING"),
      done: translate("QMS/SUPPLIER/STATUS_DONE"),
      failed: translate("QMS/SUPPLIER/STATUS_FAILED"),
    } as const
  )[state.status]

  return (
    <div className="flex items-center gap-2 rounded px-2 py-1 hover:bg-grey3/40">
      <Icon
        size={14}
        className={clsx(
          "shrink-0",
          tint,
          state.status === "parsing" || state.status === "extracting"
            ? "animate-pulse"
            : "",
        )}
      />
      <span
        className="flex-1 truncate text-xs text-white"
        title={state.doc.filename}
      >
        {state.doc.filename}
      </span>
      <span className={clsx("shrink-0 text-[10px]", tint)}>{label}</span>
    </div>
  )
}
